/**
 * CacheManager - Dual-tier caching with localStorage and IndexedDB
 * Migrated from multi-strategy-signals.html
 */

import LZString from 'lz-string';
import { openDB } from 'idb';

const CACHE_VERSION = '2.0';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const DB_NAME = 'trader-reports-cache';
const DB_VERSION = 1;

class CacheManager {
  constructor() {
    this.db = null;
    this.initDB();
  }

  async initDB() {
    this.db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('chartData')) {
          db.createObjectStore('chartData', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
      }
    });
  }

  // LocalStorage methods (for list data)
  setLocal(key, data) {
    try {
      const item = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      const compressed = LZString.compress(JSON.stringify(item));
      localStorage.setItem(key, compressed);
      return true;
    } catch (err) {
      console.warn('LocalStorage set failed:', err);
      return false;
    }
  }

  getLocal(key) {
    try {
      const compressed = localStorage.getItem(key);
      if (!compressed) return null;

      const item = JSON.parse(LZString.decompress(compressed));
      if (!item) return null;

      // Check version
      if (item.version !== CACHE_VERSION) {
        this.removeLocal(key);
        return null;
      }

      // Check TTL
      if (Date.now() - item.timestamp > CACHE_TTL) {
        this.removeLocal(key);
        return null;
      }

      return item.data;
    } catch (err) {
      console.warn('LocalStorage get failed:', err);
      return null;
    }
  }

  removeLocal(key) {
    localStorage.removeItem(key);
  }

  // IndexedDB methods (for large chart data)
  async setIndexedDB(key, data) {
    try {
      if (!this.db) await this.initDB();

      const item = {
        key,
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };

      await this.db.put('chartData', item);
      return true;
    } catch (err) {
      console.warn('IndexedDB set failed:', err);
      return false;
    }
  }

  async getIndexedDB(key) {
    try {
      if (!this.db) await this.initDB();

      const item = await this.db.get('chartData', key);
      if (!item) return null;

      // Check version
      if (item.version !== CACHE_VERSION) {
        await this.removeIndexedDB(key);
        return null;
      }

      // Check TTL
      if (Date.now() - item.timestamp > CACHE_TTL) {
        await this.removeIndexedDB(key);
        return null;
      }

      return item.data;
    } catch (err) {
      console.warn('IndexedDB get failed:', err);
      return null;
    }
  }

  async removeIndexedDB(key) {
    try {
      if (!this.db) await this.initDB();
      await this.db.delete('chartData', key);
    } catch (err) {
      console.warn('IndexedDB remove failed:', err);
    }
  }

  // Cleanup old entries
  async cleanup() {
    try {
      if (!this.db) await this.initDB();

      const tx = this.db.transaction('chartData', 'readwrite');
      const store = tx.objectStore('chartData');
      const allKeys = await store.getAllKeys();

      for (const key of allKeys) {
        const item = await store.get(key);
        if (item && Date.now() - item.timestamp > CACHE_TTL) {
          await store.delete(key);
        }
      }

      await tx.done;
    } catch (err) {
      console.warn('Cleanup failed:', err);
    }
  }

  // Clear all caches
  async clearAll() {
    localStorage.clear();
    if (this.db) {
      await this.db.clear('chartData');
      await this.db.clear('metadata');
    }
  }
}

export default new CacheManager();
